import React from 'react';

// Filter types and state definitions (self-contained)
export type FilterType = 'temperature' | 'pressure' | 'humidity' | 'precipitation' | 'SM' | 'SMRZ';
export interface FilterState {
  filters: Record<FilterType, any>;
  activeFilters: FilterType[];
}

interface LegendProps {
  filter: FilterState;
}

// Define color ranges for the legend
const getColorsForParameter = (parameter: FilterType): { color: string, label: string }[] => {
  switch (parameter) {
    case 'temperature':
      return [
        { color: '#C2B28A', label: '0°C - 10°C' },
        { color: '#D9B17C', label: '10°C - 25°C' },
        { color: '#C27D60', label: '25°C - 40°C' },
        { color: '#941C30', label: '40°C - 50°C' },
      ];
    case 'pressure':
      return [
        { color: '#C27D60', label: '99 - 100 kPa' },
        { color: '#D9B17C', label: '100 - 101 kPa' },
        { color: '#C2B28A', label: '101 - 102 kPa' },
        { color: '#F2ECDD', label: '102 - 103 kPa' },
      ];
    case 'humidity':
      return [
        { color: '#941C30', label: '0% - 30%' },
        { color: '#C27D60', label: '30% - 50%' },
        { color: '#D9B17C', label: '50% - 70%' },
        { color: '#C2B28A', label: '70% - 100%' },
      ];
    case 'precipitation':
      return [
        { color: '#b3c6ff', label: '0 - 10 mm' },
        { color: '#6699ff', label: '10 - 50 mm' },
        { color: '#3366cc', label: '50 - 100 mm' },
        { color: '#003399', label: '100 - 200 mm' },
      ];
    case 'SM':
    case 'SMRZ':
      return [
        { color: '#e0f7e9', label: '0.00 - 0.10' },
        { color: '#7ed957', label: '0.10 - 0.20' },
        { color: '#2ecc40', label: '0.20 - 0.30' },
        { color: '#145a32', label: '0.30 - 1.00' },
      ];
    default:
      return [{ color: '#941C30', label: 'No Filter' }];
  }
};

// Parameter display names
const getParameterDisplayName = (type: FilterType): string => {
  switch (type) {
    case 'temperature': return 'Temperature';
    case 'pressure': return 'Pressure';
    case 'humidity': return 'Humidity';
    case 'precipitation': return 'Precipitation';
    case 'SM': return 'Soil Moisture (Surface)';
    case 'SMRZ': return 'Soil Moisture (Rootzone)';
  }
};

const Legend: React.FC<LegendProps> = ({ filter }) => {
  let legendItems: { color: string; label: string }[] = [];
  let parameterName = 'No Active Filter';
  // If there's at least one active filter, use the first one for the legend
  if (filter.activeFilters.length > 0) {
    const primaryFilter = filter.activeFilters[0];
    legendItems = getColorsForParameter(primaryFilter);
    parameterName = getParameterDisplayName(primaryFilter);
  } else {
    // Default legend when no filters are active
    legendItems = [{ color: '#941C30', label: 'All Data Points' }];
  }

  return (
    <div className="map-legend glass-panel">
      <h4>Legend</h4>
      <div className="legend-note">
        {filter.activeFilters.length > 0
          ? `Color scale for ${parameterName}`
          : 'No filters applied - all data points shown'}
      </div>
      <div className="legend-items">
        {legendItems.map((item, index) => (
          <div key={index} className="legend-item">
            <div className="color-box" style={{ backgroundColor: item.color }}></div>
            <span className="legend-label">{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Legend; 